import { watch } from 'vue'
import { useQueueStore } from '@stores/queue'
import { usePlaybackStore } from '@stores/playback'
import messages from '@data/messages'
import DataLayer from '@services/data-layer/index'
import { useApi } from '@composables/use-api'
import Queue from '@data/models/Queue'
import { useNowPlayingStore } from '@stores/now-playing'
import { useAppearanceStore } from '@stores/appearance'
import { useAuthStore } from '@stores/auth'
import { usePremiumStore } from '@stores/premium'
import localSave from '@utils/local-save'
import analyticsService from './analytics-service'
import settingsService from './settings-service'
import modalService from './modal-service'
import { onDemandQualityOptions  as qualityOptions } from './../configuration'
import playbackTracker from './mixpanel/playback-tracker'
import paywallTracker from './mixpanel/paywall-tracker'
import {
    CONTENT_TYPES,
    ATTRIBUTION_SOURCES,
    SOURCE_SCREENS,
    WALL_TYPES,
    PRESENTATION_TYPES,
    TRIGGER_REASONS,
    ENTRY_POINTS,
    ERROR_STAGES,
    PLAYER_STATES,
    ENDED_REASONS
} from './mixpanel/analytics-spec'

const DEFAULT_ON_DEMAND_QUALITY = '96'

export default ({ playerService }) => {
    const data = {
        resource: null,
        mix: null,
        queue: new Queue(),
        activePlayingId: null,
    }

    let prevStatus = null

    // Holds the unsubscribe fn for the current radio now-playing subscription,
    // so a new play (or switching to a mix) tears down the previous one instead
    // of stacking forever-firing subscribers.
    let nowPlayingUnsubscribe = null
    const stopNowPlaying = () => {
        if (nowPlayingUnsubscribe) {
            nowPlayingUnsubscribe()
            nowPlayingUnsubscribe = null
        }
    }

    const syncPlayback = () => {
        usePlaybackStore().setData(data)
        useQueueStore().setData(data)
    }

    const notifyError = ({ message, type }) => useAppearanceStore().handleNotification({ message, type })

    const currentUser = () => useAuthStore().currentUser || null

    const resolveUserQuality = async () => {
        const key = await settingsService.getCurrentOnDemandQualityKey()
        const qualityOption = qualityOptions[key] || qualityOptions.low || {}
        return qualityOption.value || DEFAULT_ON_DEMAND_QUALITY
    }

    const presentPremiumUpsell = entryPoint => {
        paywallTracker.onWallShown({
            wallType: WALL_TYPES.STREAMING_BLOCKED,
            presentation: PRESENTATION_TYPES.MODAL,
            triggerReason: TRIGGER_REASONS.PLAYBACK_BLOCKED,
            entryPoint,
        })
        const isEntitledFreeTrial = !usePremiumStore().hasEverBeenPremium
        modalService.showPremiumUpsell({
            headline: messages.PREMIUM_UPSELL_HEADLINE,
            description: null,
            subDescription: isEntitledFreeTrial ? messages.PREMIUM_UPSELL_SUBHEADLINE : null,
            entitledFreeTrial: isEntitledFreeTrial,
            primaryAction: {
                text: isEntitledFreeTrial ? messages.PREMIUM_UPSELL_CTA_FREE_TRIAL : messages.PREMIUM_UPSELL_CTA_UPGRADE,
                route: '/premium/view-plans',
            },
            secondaryAction: {
                text: messages.PREMIUM_UPSELL_SECONDARY,
                route: '/premium/view-plans',
            },
        })
    }

    // Map player status to Mixpanel player states
    const mapStatusToPlayerState = status => {
        switch (status) {
        case 'playing': return PLAYER_STATES.PLAYING
        case 'paused': return PLAYER_STATES.PAUSED
        case 'loading': return PLAYER_STATES.BUFFERING
        default: return null
        }
    }

    // currently forwarding values from playerService - determine if we transform time in some way for radio playback ()
    const emitPlayerUpdate = val => {
        const currentStatus = val.status

        // Track audio started when transitioning to 'playing'
        if (currentStatus === 'playing' && prevStatus !== 'playing') {
            playbackTracker.onAudioStarted()
        }

        // Update player state for heartbeat tracking
        const playerState = mapStatusToPlayerState(currentStatus)
        if (playerState) {
            playbackTracker.updatePlayerState(playerState)
        }

        // Update playhead for on-demand content
        if (val.type === 'mix' && val.currentTime >= 0) {
            playbackTracker.updatePlayhead(Math.round(val.currentTime / 1000))
        }

        // Track user stop
        if (currentStatus === 'stopped' && prevStatus && prevStatus !== 'stopped') {
            playbackTracker.endSession(ENDED_REASONS.USER_STOP)
        }

        prevStatus = currentStatus
        usePlaybackStore().setStatus(val)
    }

    const timeUpdateHandler = val => {
        emitPlayerUpdate(val)
        val.currentTime >= val.duration - 1000 && playNext()
    }

    playerService.on('statusUpdate', emitPlayerUpdate)
    playerService.on('timeUpdate', emitPlayerUpdate)

    function _trackPremiumUpsell() {
        analyticsService.premiumUpsellInitiated()
    }

    const playMix = async (mix, startTime = 0, shouldClearQueue = true, type = 'mix') => {
        stopNowPlaying() // stop the radio subscription
        const userQuality = await resolveUserQuality()

        if (data.mix && data.mix.id !== mix.id) playerService.stop()

        shouldClearQueue && clearQueue()

        return new Promise((resolve, reject) => {
            if (currentUser()?.token) {
                data.mix = data.resource = mix
                data.resource.type = type
                data.activePlayingId = data.mix.id

                // Start Mixpanel playback session
                playbackTracker.startSession({
                    contentType: CONTENT_TYPES.ON_DEMAND,
                    contentId: String(mix.id),
                    sourceScreen: SOURCE_SCREENS.MIX_DETAIL,
                    attributionSource: ATTRIBUTION_SOURCES.DIRECT
                })

                playerService.off('timeUpdate')

                playerService.on('timeUpdate', val => {
                    timeUpdateHandler(val)
                })

                syncPlayback()

                useApi()
                    .mixes.url({ id: mix.id, quality: userQuality })
                    .then(({ body }) => {
                        if (!(body && body.url)) {
                            throw new Error(messages.SOMETHING_WENT_WRONG)
                        }

                        playerService.play(body.url, startTime, 'mix')
                        analyticsService.playMedia({
                            event: 'Mix Play',
                            mix: (body.resource && body.resource.title) || mix.title,
                        })
                        resolve(true)
                    })
                    .catch(e => {
                        if (e.response && e.response.status === 403) {
                            // Track playback error
                            playbackTracker.onPlaybackError(
                                ERROR_STAGES.REQUEST,
                                '403',
                                'Premium content blocked'
                            )

                            data.mix = data.resource = null
                            clearQueue()

                            syncPlayback()
                            _trackPremiumUpsell({})

                            presentPremiumUpsell(ENTRY_POINTS.MIX_PLAY_BLOCKED)
                            return
                        }

                        reject(e)
                    })
            } else {
                reject(new Error(messages.PLAYER_PREMIUM_ERROR))
            }
        })
    }

    const playRadioChannel = channelId => {
        let playbackInitiated = false
        if (!channelId) return false
        stopNowPlaying() // drop any previous channel's subscription first
        clearQueue()
        data.activePlayingId = channelId
        let currentRetries = 0

        // Start Mixpanel playback session for radio
        playbackTracker.startSession({
            contentType: CONTENT_TYPES.RADIO,
            contentId: String(channelId),
            sourceScreen: SOURCE_SCREENS.RADIO_PLAYER,
            attributionSource: ATTRIBUTION_SOURCES.DIRECT
        })

        const token = currentUser()?.token || ''

        let channel = null
        let subscribed = false

        const fetchChannel = async () => {
            const response = await DataLayer.channels.get()
            return Object.values(response.body).find(chnl => chnl.id === channelId)
        }

        const subscribeNowPlaying = () => {
            if (subscribed) return
            subscribed = true
            nowPlayingUnsubscribe = watch(
                () => useNowPlayingStore().byStation[channelId],
                handleNowPlaying,
                { immediate: true }
            )
        }

        const handleNowPlaying = list => {
            if (data.activePlayingId !== channelId) return false
            if (!channel || !channel.servers) return false
            const nowPlaying = (list || [])[0] || {}
            data.resource = { ...channel, type: 'channel', nowPlaying: [nowPlaying] }
            data.mix = nowPlaying.mixes || null
            syncPlayback()
            return true
        }

        const checkStreamError = async (station, selectMount, currentToken) => {
            const { mount, server } = await selectMount
            try {
                await DataLayer.channels.validateStream({ token: currentToken, station, mount })
                subscribeNowPlaying()
                analyticsService.playMedia({ event: 'Radio Play', channel: data.resource.title })
                return server
            } catch (e) {
                handleError(e)
            }
        }

        const busyServerHandler = () => {
            notifyError({ message: messages.SERVER_BUSY_TRY_AGAIN, type: 'warning' })
        }

        const selectStreamServer = async servers => {
            const userQuality = await resolveUserQuality()
            const requestQuality = Number(userQuality) >= 320 ? 'high' : userQuality >= 128 ?  'mid' : 'low'
            servers = JSON.parse(JSON.stringify(servers))
            if (!servers) return
            const MAX_RETRIES = 4
            const options = Object.keys(servers).filter(key => key.includes(requestQuality))
            if (currentRetries > MAX_RETRIES) return busyServerHandler()
            const mount = options[currentRetries % options.length]
            const selectedServer = servers[mount].urls[0]
            currentRetries++
            return { server: selectedServer, mount }
        }

        const playRequest = async () => {
            if (playbackInitiated) return
            channel = await fetchChannel()
            if (!channel) return
            data.resource = { ...channel, type: 'channel' }
            syncPlayback()
            const server = await checkStreamError(
                channelId,
                selectStreamServer(channel.servers.items),
                token
            )
            await playerService.play(`${server}${token ? '?token=' + token : ''}`, 0, 'radio')
            subscribeNowPlaying()
            playbackInitiated = true
        }

        const handleError = error => {
            data.resource = null
            clearQueue()
            syncPlayback()

            switch (error.response.status) {
            case 401:
                // Track playback error for premium required
                playbackTracker.onPlaybackError(
                    ERROR_STAGES.REQUEST,
                    '401',
                    'Premium required for radio streaming'
                )

                presentPremiumUpsell(ENTRY_POINTS.RADIO_PLAY_BLOCKED)
                break

            case 403:
                // Track playback error for auth required
                playbackTracker.onPlaybackError(
                    ERROR_STAGES.REQUEST,
                    '403',
                    'Authentication required for radio streaming'
                )
                modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
                break

            case 503:
                // Server busy - will retry, don't track as error yet
                playRequest()
                break

            default:
                // Track generic playback error
                playbackTracker.onPlaybackError(
                    ERROR_STAGES.REQUEST,
                    String(error.response?.status || 'unknown'),
                    'Radio streaming error'
                )
                notifyError({ message: messages.SOMETHING_WENT_WRONG, type: 'warning' })
                break
            }
        }

        playerService.off('timeUpdate')

        playerService.on('timeUpdate', val => emitPlayerUpdate(val))

        return playRequest()
    }

    const playMixMultiple = ({ mixes, position = 0, id = null }) => {
        if (data.queue && data.queue.playlistId === id && data.queue.position === position) return pause()

        createQueue({ mixes, position, id })
        playMix(mixes[position], 0, false, 'playlist')
    }

    const playPrevious = () => {
        if (!data.queue || !data.queue.mixes.length || data.queue.position <= 0) return

        data.queue.position--
        playMix(data.queue.mixes[data.queue.position], 0, false, 'playlist')
    }

    const playNext = () => {
        if (!data.queue || !data.queue.mixes.length || data.queue.position >= data.queue.mixes.length - 1) {
            // Track finished, no more in queue - end session with track_end
            playbackTracker.endSession(ENDED_REASONS.TRACK_END)
            return
        }

        data.queue.position++
        playMix(data.queue.mixes[data.queue.position], 0, false, 'playlist')
    }

    const addToQueue = ({ mixes, queuePosition, playlistId = null }) => {
        const queue = data.queue ? [...data.queue] : new Queue()
        queue.playlistId = playlistId
        queue.mixes.insert(queuePosition, [...mixes])
        data.queue = queue
        localSave.save('activeQueue', queue)
    }

    const createQueue = ({ mixes, position, id }) => {
        const queue = new Queue(mixes, position, id)
        data.queue = queue
        localSave.save('activeQueue', queue)
    }

    const clearQueue = () => {
        const queue = new Queue()
        data.queue = queue
        localSave.save('activeQueue', queue)
    }

    const pause = () => {
        if (data.resource && data.resource.type === 'channel') {
            if (usePlaybackStore().status === 'stopped') {
                playRadioChannel(data.resource.id)
            } else {
                playerService.stop()
            }
        } else {
            playerService.pause()
        }
    }

    const setQueuePosition = position => {
        data.queue.position = position
    }

    return {
        playMix,
        playRadioChannel,
        playMixMultiple,
        playPrevious,
        playNext,
        addToQueue,
        clearQueue,
        setQueuePosition,
        pause,
    }
}
